/**
 * ContentPlannerWizard - Multi-step wizard for content creation
 *
 * Supports two paths:
 * - Path A (4 steps): Have keywords → Keywords → Template → Review
 * - Path B (6 steps): Find keywords → Research → Clusters → Keywords → Template → Review
 */

import React, { useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ClusterSummary } from '@/types/contentPlanner';

// Step components (to be created)
import EntrySelectionStep from './wizard-steps/EntrySelectionStep';
import KeywordsInputStep from './wizard-steps/KeywordsInputStep';
import ResearchStep from './wizard-steps/ResearchStep';
import ClustersViewStep from './wizard-steps/ClustersViewStep';
import TemplateSelectionStep from './wizard-steps/TemplateSelectionStep';
import ReviewStep from './wizard-steps/ReviewStep';
import { SuggestedTemplateBanner } from './shared/SuggestedTemplateBanner';

export type EntryPoint = 'have-keywords' | 'find-keywords';

export type PublishDestination =
  | 'none'
  | 'wp_draft'
  | 'gdocs'
  | 'wp_draft_gdocs'
  | 'wp_publish'
  | 'wp_publish_gdocs';

export type WordPressPublishMode = 'none' | 'draft' | 'publish';

export const getWordPressPublishMode = (destination: PublishDestination): WordPressPublishMode => {
  if (destination === 'wp_publish' || destination === 'wp_publish_gdocs') return 'publish';
  if (destination === 'wp_draft' || destination === 'wp_draft_gdocs') return 'draft';
  return 'none';
};

export const shouldExportToGdocs = (destination: PublishDestination): boolean =>
  destination === 'gdocs' || destination === 'wp_draft_gdocs' || destination === 'wp_publish_gdocs';

export interface WizardState {
  // Common
  entryPoint: EntryPoint | null;
  keywords: string[];
  templateId: string | null;
  targetWordCount: number;
  publishDestination: PublishDestination;
  blogTitle: string;
  selectedTopic: string;

  // Research flow specific
  seedKeyword: string;
  selectedRegion: string;
  selectedMapId: string | null;
  selectedClusters: ClusterSummary[];
}

interface ContentPlannerWizardProps {
  // Initial values from URL params
  initialTemplateId?: string | null;
  initialKeywords?: string[];
  initialStep?: number;
  initialEntryPoint?: EntryPoint | null;
  isSubmitting?: boolean;

  // Callbacks
  onComplete: (state: WizardState) => void;
  onCancel: () => void;
}

// Step configuration based on entry point
const getStepsForPath = (entryPoint: EntryPoint | null): string[] => {
  if (!entryPoint) {
    return ['Choose Path'];
  }

  if (entryPoint === 'have-keywords') {
    return [
      'Choose Path',
      'Keywords',
      'Template',
      'Review'
    ];
  }

  return [
    'Choose Path',
    'Research',
    'Clusters',
    'Keywords',
    'Template',
    'Review'
  ];
};

const ContentPlannerWizard: React.FC<ContentPlannerWizardProps> = ({
  initialTemplateId,
  initialKeywords = [],
  initialStep = 0,
  initialEntryPoint = null,
  isSubmitting = false,
  onComplete,
  onCancel,
}) => {
  // Wizard state
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [wizardState, setWizardState] = useState<WizardState>({
    entryPoint: initialEntryPoint,
    keywords: initialKeywords,
    templateId: initialTemplateId || null,
    targetWordCount: 1800,
    publishDestination: 'none',
    blogTitle: '',
    selectedTopic: '',
    seedKeyword: '',
    selectedRegion: 'US',
    selectedMapId: null,
    selectedClusters: [],
  });

  // Get steps for current path
  const steps = getStepsForPath(wizardState.entryPoint);
  const isLastStep = currentStep === steps.length - 1;

  // Update wizard state
  const updateState = useCallback((updates: Partial<WizardState>) => {
    setWizardState(prev => ({ ...prev, ...updates }));
  }, []);

  // Navigation
  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step < steps.length) {
      setCurrentStep(step);
    }
  }, [steps.length]);

  const goNext = useCallback(() => {
    if (!isLastStep) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Final step - complete wizard
      onComplete(wizardState);
    }
  }, [isLastStep, onComplete, wizardState]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else {
      onCancel();
    }
  }, [currentStep, onCancel]);

  // Validate current step
  const isStepValid = useCallback((): boolean => {
    const stepName = steps[currentStep];

    switch (stepName) {
      case 'Choose Path':
        return wizardState.entryPoint !== null;

      case 'Keywords':
        return wizardState.keywords.length > 0 && wizardState.keywords.length <= 50;

      case 'Research':
        return wizardState.seedKeyword.trim().length > 0;

      case 'Clusters':
        return wizardState.selectedClusters.length > 0;

      case 'Template':
        return true; // Template is optional

      case 'Review':
        return true;

      default:
        return false;
    }
  }, [currentStep, wizardState, steps]);

  // Render current step
  const renderStep = () => {
    const stepName = steps[currentStep];

    switch (stepName) {
      case 'Choose Path':
        return (
          <EntrySelectionStep
            selectedPath={wizardState.entryPoint}
            onSelectPath={(path) => updateState({ entryPoint: path })}
          />
        );

      case 'Keywords':
        return (
          <KeywordsInputStep
            keywords={wizardState.keywords}
            onUpdateKeywords={(keywords) => updateState({ keywords })}
            blogTitle={wizardState.blogTitle}
            onUpdateBlogTitle={(blogTitle) => updateState({ blogTitle })}
            selectedTopic={wizardState.selectedTopic}
            onUpdateTopic={(selectedTopic) => updateState({ selectedTopic })}
          />
        );

      case 'Research':
        return (
          <ResearchStep
            seedKeyword={wizardState.seedKeyword}
            region={wizardState.selectedRegion}
            onUpdateSeedKeyword={(seedKeyword) => updateState({ seedKeyword })}
            onUpdateRegion={(selectedRegion) => updateState({ selectedRegion })}
          />
        );

      case 'Clusters':
        return (
          <ClustersViewStep
            mapId={wizardState.selectedMapId}
            selectedClusters={wizardState.selectedClusters}
            onSelectClusters={(clusters) => updateState({ selectedClusters: clusters })}
          />
        );

      case 'Template':
        return (
          <div className="space-y-4">
            <SuggestedTemplateBanner
              keyword={wizardState.keywords[0] || ''}
              title={wizardState.blogTitle || undefined}
              selectedTemplateId={wizardState.templateId}
              onSelect={(templateId) => updateState({ templateId })}
            />
            <TemplateSelectionStep
              selectedTemplateId={wizardState.templateId}
              onSelectTemplate={(templateId, wordCount) =>
                updateState({
                  templateId,
                  ...(wordCount ? { targetWordCount: wordCount } : {}),
                })
              }
            />
          </div>
        );

      case 'Review':
        return (
          <ReviewStep
            wizardState={wizardState}
            onEdit={(step) => goToStep(step)}
            onUpdateWordCount={(targetWordCount) => updateState({ targetWordCount })}
            onUpdatePublishDestination={(publishDestination) => updateState({ publishDestination })}
          />
        );

      default:
        return <div>Unknown step</div>;
    }
  };

  // Determine if current step is optional
  const isStepOptional = steps[currentStep] === 'Template';

  // Determine next button text
  const getNextButtonText = () => {
    if (isLastStep) return 'Create Content';
    if (isStepOptional && !wizardState.templateId) return 'Skip';
    return 'Next';
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Stepper */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;
            const isClickable = isCompleted;

            return (
              <React.Fragment key={step}>
                <button
                  onClick={() => isClickable && goToStep(index)}
                  disabled={!isClickable}
                  className={cn(
                    'flex items-center gap-2 transition-all',
                    isClickable && 'cursor-pointer hover:opacity-80',
                    !isClickable && 'cursor-not-allowed'
                  )}
                >
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all',
                      isCompleted && 'bg-green-600 text-white',
                      isCurrent && 'bg-blue-600 text-white ring-4 ring-blue-100',
                      !isCompleted && !isCurrent && 'bg-gray-200 text-muted-foreground'
                    )}
                  >
                    {isCompleted ? '✓' : index + 1}
                  </div>
                  <span
                    className={cn(
                      'text-sm font-medium transition-all hidden md:inline',
                      isCurrent && 'text-blue-600 dark:text-blue-400',
                      isCompleted && 'text-green-600 dark:text-green-400',
                      !isCompleted && !isCurrent && 'text-muted-foreground'
                    )}
                  >
                    {step}
                  </span>
                </button>

                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'flex-1 h-0.5 mx-2 transition-all',
                      isCompleted ? 'bg-green-600' : 'bg-gray-200'
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <Card className="p-6 min-h-[400px]">
        {renderStep()}
      </Card>

      {/* Footer Navigation */}
      <div className="flex items-center justify-between mt-6">
        <Button
          variant="outline"
          onClick={goBack}
          disabled={currentStep === 0}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex items-center gap-2">
          {isStepOptional && !wizardState.templateId && (
            <span className="text-sm text-muted-foreground mr-2">
              (Optional)
            </span>
          )}
          <Button
            onClick={goNext}
            disabled={!isStepValid() || isSubmitting}
            className={cn(
              isLastStep && 'bg-green-600 hover:bg-green-700'
            )}
          >
            {isSubmitting ? 'Creating...' : getNextButtonText()}
            {!isLastStep && !isSubmitting && <ArrowRight className="h-4 w-4 ml-2" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContentPlannerWizard;
